import React, { useEffect, useMemo, useState } from 'react';

interface DynamicComponentRendererProps {
  componentCode: string;
  data: any;
  props: any;
}

let babelLoader: Promise<any> | null = null;
const loadBabel = () => {
  if (!babelLoader) {
    babelLoader = import('@babel/standalone');
  }
  return babelLoader;
};

export default function DynamicComponentRenderer({ componentCode, data, props }: DynamicComponentRendererProps) {
  const [RenderFn, setRenderFn] = useState<React.ComponentType | null>(null);
  const [errorText, setErrorText] = useState<string | null>(null);

  const executionContext = useMemo(() => ({
    React,
    useState: React.useState,
    useEffect: React.useEffect,
    useMemo: React.useMemo,
    useCallback: React.useCallback,
    data,
    ...props
  }), [data, props]);

  useEffect(() => {
    let cancelled = false;
    const compileAndSet = async () => {
      setErrorText(null);
      setRenderFn(null);
      try {
        const buildWrapped = (code: string) => `
          (function() {
            const { React, useState, useEffect, useMemo, useCallback, data, ...props } = arguments[0];
            ${code}
            return (typeof CustomComponent !== 'undefined' && CustomComponent) 
              || (typeof DataTable !== 'undefined' && DataTable)
              || (typeof MetricCards !== 'undefined' && MetricCards)
              || function() { 
                   return React.createElement('div', { className: 'p-4 text-red-600' }, 'Component failed to render'); 
                 };
          })`;

        // Sanitize common patterns returned by AI
        let codeToEval = componentCode
          // remove any import lines
          .replace(/\n?import[^;]*;?/g, '')
          // remove export default (we return the function explicitly)
          .replace(/export\s+default\s+/g, '')
          // ensure function name exists
          ;
        // If JSX is present, transpile via Babel at runtime
        if (/<[A-Za-z]/.test(componentCode)) {
          const Babel = await loadBabel();
          const transformed = Babel.transform(codeToEval, {
            presets: ['react', ['typescript', { isTSX: true, allExtensions: true }]],
            filename: 'component.tsx',
            sourceType: 'module'
          });
          codeToEval = transformed.code;
        }

        const componentFactory = eval(buildWrapped(codeToEval));
        const Component = componentFactory(executionContext);
        if (!cancelled) {
          setRenderFn(() => (propsInner: any) => React.createElement(Component, { data, ...props, ...propsInner }));
        }
      } catch (err: any) {
        // If first attempt failed due to JSX, try transpiling as fallback
        if (err instanceof SyntaxError && /Unexpected token/.test(String(err))) {
          try {
            const Babel = await loadBabel();
            const sanitized = componentCode
              .replace(/\n?import[^;]*;?/g, '')
              .replace(/export\s+default\s+/g, '');
            const transformed = Babel.transform(sanitized, {
              presets: ['react', ['typescript', { isTSX: true, allExtensions: true }]],
              filename: 'component.tsx',
              sourceType: 'module'
            });
            const componentFactory = eval(buildWrapped(transformed.code));
            const Component = componentFactory(executionContext);
            if (!cancelled) {
              setRenderFn(() => (propsInner: any) => React.createElement(Component, { data, ...props, ...propsInner }));
            }
            return;
          } catch (innerErr: any) {
            if (!cancelled) setErrorText(String(innerErr));
            return;
          }
        }
        if (!cancelled) setErrorText(String(err));
      }
    };
    compileAndSet();
    return () => { cancelled = true; };
  }, [componentCode, executionContext, data, props]);

  if (errorText) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <h3 className="text-red-800 font-semibold">Component Error</h3>
        <p className="text-red-600 text-sm mt-2">Failed to render dynamic component. Check details below.</p>
        <pre className="text-xs text-red-700 mt-2 overflow-auto">{errorText}</pre>
      </div>
    );
  }

  if (!RenderFn) {
    return (
      <div className="p-6 text-sm text-gray-500">Rendering custom component…</div>
    );
  }

  return <RenderFn />;
}
